"use client";

import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation, Scrollbar, Autoplay } from 'swiper/modules';
import '../app/homepage.css';
import Image, { StaticImageData } from "next/image";

import Testimonials from "./Testimonials";
import StatsSection from "./StatusSection";
import GlobalPartnersCount from "@/components/GlobalPartnersCount";
import { VILVAD2C_STATS, VILVAD2C_UPDATED_AT } from "@/lib/vilvaD2CStats";
import UpdatedAt from "@/components/UpdatedAt";
import StatNumber from "@/components/StatNumber";
import EcomPricingPlan from "./EcomPricingPlan";
import { m, Variants } from "framer-motion"; // Import framer-motion
import { useInView } from "react-intersection-observer"; // Import react-intersection-observer
import { Icon } from "@radix-ui/react-select";
import image from "next/image";
import DashBoard from "./customer/DashBoard";
import FaqSection from "./Homepagefaq";
import { getImageUrl } from "@/lib/imageUrl";
import TrustedPartners from "./TrustedPartners";
import CircularTestimonialView from "./CircularTestimonialView";


const VilvaD2C = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeContent, setActiveContent] = useState("new_content1");
  const contentRefs = useRef({});
  // Add this ref at the top with your other refs
  const [everythingRef, everythingInView] = useInView({ triggerOnce: true, threshold: 0.2 });


  useEffect(() => {
    const headings = document.querySelectorAll(".new_heading");
    headings.forEach((heading) => {
      heading.addEventListener("click", () => {
        const contentId = heading.getAttribute("data-content");
        if (contentId) { // Type guard to ensure contentId is not null
          setActiveContent(contentId);
          const targetElement = contentRefs.current[contentId];
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      });
    });

    return () => {
      headings.forEach((heading) => {
        heading.removeEventListener("click", () => { });
      });
    };
  }, []);

  const toggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Animation variants for fade-in effect
  const fadeIn: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const useIsMobile = (breakpoint = 768) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const check = () => setIsMobile(window.innerWidth < breakpoint);
      check();
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }, [breakpoint]);

    return isMobile;
  };

  // Hook for in-view detection
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [allInOneRef, allInOneInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [storeRef, storeInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [clientsRef, clientsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [dashboardRef, dashboardInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [pricingRef, pricingInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [testimonialsRef, testimonialsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [statsSectionRef, statsSectionInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [faqRef, faqInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [freedomRef, freedomInView] = useInView({ triggerOnce: true, threshold: 0.2 });


  // Display strings + the exact figures behind them (see lib/vilvaD2CStats.ts).
  const stats = VILVAD2C_STATS;

  const Icons = {
    Tick: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <g clipPath="url(#clip0)">
          <path
            d="M14.4208 6.82669C14.726 7.13187 14.726 7.62656 14.4208 7.93158L9.17972 13.1728C8.87454 13.4778 8.38 13.4778 8.07483 13.1728L5.57986 10.6777C5.27469 10.3727 5.27469 9.87799 5.57986 9.57297C5.88489 9.26779 6.37958 9.26779 6.6846 9.57297L8.6272 11.5156L13.3159 6.82669C13.6211 6.52167 14.1158 6.52167 14.4208 6.82669ZM20.0003 9.99976C20.0003 15.5273 15.5271 19.9998 10.0003 19.9998C4.47284 19.9998 0.000335693 15.5265 0.000335693 9.99976C0.000335693 4.47226 4.4736 -0.000244141 10.0003 -0.000244141C15.5278 -0.000244141 20.0003 4.47302 20.0003 9.99976ZM18.4378 9.99976C18.4378 5.33591 14.6636 1.56226 10.0003 1.56226C5.33649 1.56226 1.56284 5.33652 1.56284 9.99976C1.56284 14.6636 5.3371 18.4373 10.0003 18.4373C14.6642 18.4373 18.4378 14.663 18.4378 9.99976Z"
            fill="#18AE2F"
          />
        </g>
        <defs>
          <clipPath id="clip0">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  };

  function FeatureCard({
    title,
    items,
    cta,
    image,
    mobileImage,
  }: {
    title: string;
    items: string[];
    cta: string;
    image: string;           // ✅ just string
    mobileImage?: string;    // ✅ just string
  }) {
    return (
      <div className="rounded-[8px] border border-blue-100 bg-[#F2F8FF] pt-[40px] pr-[39px] pl-[24px] flex flex-col justify-between">
        <div>
          <p className="md:text-[32px] text-[22px] font-[700] text-[#0B1535] mb-6 font-NeueMontreal">
            {title}
          </p>
          <ul className="space-y-3">
            {items.map((item, index) => (
              <li key={index} className="flex items-start gap-3 text-[#4B5563]">
                <span className="h-5 w-5 flex items-center justify-center rounded-full text-green-500 text-xs">
                  {Icons.Tick}
                </span>
                <span className="text-sm font-Schibsted_Grotesk">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8">
          <picture>
            {/* Mobile */}
            <source media="(max-width: 767px)" srcSet={mobileImage ?? image} />
            {/* Desktop */}
            <source media="(min-width: 768px)" srcSet={image} />
            <img
              src={image}
              alt={title || "Feature Image"}
              className="w-full h-auto ml-[24px] overflow-hidden"
            />
          </picture>
        </div>
      </div>
    );
  }

  const faqs = [
    {
      question: "What is Vilva D2C?",
      answer: `
      Vilva D2C is a powerful all-in-one e-commerce platform that helps businesses launch, manage, and grow online stores easily — with tools for product management, orders, inventory, payments, analytics, and customer engagement, all from one dashboard.
    `
    },
    {
      question: "How does Vilva D2C help my e-commerce business grow?",
      answer: `
      It helps your store scale faster by:
      <ul>
        <li>Handling high-traffic sales without downtime</li>
        <li>Managing inventory in real time</li>
        <li>Automating order processing and fulfilment</li>
        <li>Personalising customer experiences</li>
        <li>Giving data-driven insights to optimise growth</li>
      </ul>
      For example, one brand using Vilva D2C achieved lakhs in sales within days by managing traffic, inventory, and engagement effortlessly.
    `
    },
    {
      question: "Do I need technical skills to use the platform?",
      answer: `
      No. Vilva D2C is built for business owners and teams — no coding required. You can set up your store, manage products and orders, and track performance without technical knowledge.
    `
    },
    {
      question: "Can Vilva D2C handle large traffic and peak sales?",
      answer: `
      Yes. It’s designed to scale with your business. Cloud-ready performance keeps your store live and responsive even when thousands of customers visit at once.
    `
    },
    {
      question: "What e-commerce features does Vilva D2C include?",
      answer: `
      Typical features expected from a modern SaaS e-commerce platform include:
      <ul>
        <li>Product catalogue and inventory management</li>
        <li>Secure payment processing</li>
        <li>Order tracking and fulfilment</li>
        <li>Customer profiles and CRM tools</li>
        <li>Marketing and engagement tools</li>
        <li>Analytics and reporting</li>
      </ul>
      These features help you run a complete online store from one platform.
    `
    },
    {
      question: "Can I sell on multiple channels with Vilva D2C?",
      answer: `
      Yes. Vilva D2C supports selling across web, social commerce, and marketplaces — helping you meet customers where they shop.
    `
    },
    {
      question: "How soon can I launch my online store?",
      answer: `
      Many businesses can launch a fully functional e-commerce site within a few days with Vilva D2C — much faster than building a custom platform from scratch.
    `
    },
    {
      question: "Is my store secure with Vilva D2C?",
      answer: `
      Absolutely. Modern e-commerce SaaS platforms are built with secure infrastructure, encrypted payments, and data protection measures to keep your customers and business safe.
    `
    },
    {
      question: "Will Vilva D2C help me understand my sales and customers?",
      answer: `
      Yes. Built-in analytics and dashboards provide insights into sales performance, customer behaviour, inventory trends, and growth opportunities so you can make smarter business decisions.
    `
    },
    {
      question: "Why should I choose Vilva D2C over other e-commerce solutions?",
      answer: `
      Because it’s not just a store builder — it’s a growth engine. You get:
      <ul>
        <li style="display:flex; gap:8px; align-items:flex-start;">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
          Scalable performance
        </li>
        <li style="display:flex; gap:8px; align-items:flex-start;">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
          Automated business tools
        </li>
        <li style="display:flex; gap:8px; align-items:flex-start;">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
          Data-driven insights
        </li>
        <li style="display:flex; gap:8px; align-items:flex-start;">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
          Easy management
        </li>
        <li style="display:flex; gap:8px; align-items:flex-start;">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
          Support from the Vilva Business team
        </li>
      </ul>
    `
    }
  ];



  // D2C client store showcase — the 9 current D2C stores, in display order.
  // NOTE: thingalessentials / brassidols / polivueco / aathiraicraftz / natrilnaturals
  // screenshots are not yet uploaded to the CDN at /vilvabusiness/website/mobile-img/<file>
  // (404 as of this writing) — upload them there to replace the broken images.
  const mobileSlides = [
    { href: "https://www.tamiltshirts.in/", src: getImageUrl("/vilvabusiness/website/mobile-img/tamiltshirts.webp"), alt: "Tamil Tshirts" },
    { href: "https://thingalessentials.com/", src: getImageUrl("/vilvabusiness/website/mobile-img/thingal.webp"), alt: "Thingal Essentials" },
    { href: "https://www.magizhhandicrafts.com/", src: getImageUrl("/vilvabusiness/website/mobile-img/magizh.webp"), alt: "Magizh Handicrafts" },
    { href: "https://www.adyarhandicrafts.com/", src: getImageUrl("/vilvabusiness/website/mobile-img/adyar.webp"), alt: "Adyar Handicrafts" },
    { href: "https://brassidols.com/", src: getImageUrl("/vilvabusiness/website/mobile-img/brass.webp"), alt: "Brass Idols" },
    { href: "https://www.polivueco.com/", src: getImageUrl("/vilvabusiness/website/mobile-img/polivu.webp"), alt: "PolivuEco" },
    { href: "https://www.mypolo.in/", src: getImageUrl("/vilvabusiness/website/mobile-img/mypolo.webp"), alt: "My Polo" },
    { href: "https://natrilnaturals.com/", src: getImageUrl("/vilvabusiness/website/mobile-img/natril.webp"), alt: "Natril Naturals" },
    { href: "https://www.aathiraicraftz.com/", src: getImageUrl("/vilvabusiness/website/mobile-img/aathirai.webp"), alt: "Aathirai Craftz" },
  ];


  return (
    <div className="overflow-hidden ">
      {/* Hero Section */}
      <m.section
        initial={false}
        animate="visible"
        variants={fadeIn}
        className="bg-opacity-30 hero-card mt-[20px] lg:mt-18"
      >
        <div className="mx-auto container px-3 md:px-0">
          <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-1">
            <div>
              <m.h1
                variants={fadeIn}
                initial={false}
                animate="visible"
                transition={{ delay: 0.3 }}
                className="text-center md:max-w-5xl mx-auto max-w-3xl text-[#040E56] p-0 m-0 mt-[20px] font-NeueMontreal text-[40px] md:text-[40px] lg:text-[68px] font-[700] leading-[120%]"
              >
                Build and Scale a High-<span className="font-NeueMontreal text-[40px] md:text-[40px] lg:text-[68px]  font-[700] leading-[120%]">Performance Online Store</span>
              </m.h1>
              <m.p
                variants={fadeIn}
                initial={false}
                animate="visible"
                transition={{ delay: 0.4 }}
                className="md:max-w-[810px] mx-auto max-w-3xl text-[#373844] mt-[20px] font-Schibsted_Grotesk text-[18px] lg:text-[20px] font-normal leading-[150%] text-center"
              >
                An VilvaD2C platform designed for speed, conversions, and growth. Launch fast, sell
                <span className="block px-2 py-1 font-Schibsted_Grotesk text-[18px] lg:text-[20px] font-normal leading-[150%] text-center w-fit mx-auto">
                  globally, and manage everything from one dashboard.
                </span>
              </m.p>

              <m.div
                variants={fadeIn}
                initial={false}
                animate="visible"
                transition={{ delay: 0.5 }}
                className="flex items-center justify-center mt-[20px] gap-3"
              >
                <a
                  className="text-white whitespace-nowrap font-Schibsted_Grotesk text-[16px] md:text-[18px] not-italic font-normal leading-[100%] flex h-[52px] px-[28px] md:px-[32px] py-[16px] items-center gap-[13.375px] rounded-[8px] bg-[#195AFF]"
                  href="https://admin.vilvad2c.com/register"
                  target="_blank" rel="noopener noreferrer"
                >
                  Get Started Now
                </a>
                {/* This page has two links labelled "Book Demo" pointing at different places (the
                    demo form, and the D2C sign-up in a new tab). Same text + different destination
                    is confusing on a screen reader, so each gets a distinct accessible name. */}
                <a
                  className="text-[#195AFF] whitespace-nowrap h-[52px] px-[28px] md:px-[32px] py-[16px] font-Schibsted_Grotesk text-[16px] md:text-[18px] not-italic font-normal leading-[120%] rounded-[8px] border-solid border-[1px] border-[#6F9CFF] bg-[#EFF4FF]"
                  href="/book-demo"
                  aria-label="Book a demo of Vilva D2C"
                >
                  Book Demo
                </a>
              </m.div>
            </div>
            <m.div
              variants={fadeIn}
              initial={false}
              animate="visible"
              transition={{ delay: 0.6 }}
            >

              <section className="flex items-center justify-between px-6 pointer-events-none">
                {/* <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/greview_1.webp")}
                  alt="Google Reviews rating badge for Vilva D2C"
                  width={100}
                  height={100}
                  className="md:w-fit max-w-xs md:ml-48"
                  loading="lazy"
                  decoding="async"
                /> */}
                {/* <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/jreview_1_1.webp")}
                  alt="Justdial rating badge for Vilva D2C"
                  width={100}
                  height={100}
                  className="md:w-fit max-w-xs md:mr-32"
                  loading="lazy"
                  decoding="async"
                /> */}

                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/jreview_logo_1.webp")}
                  alt="Justdial verified business badge for Vilva D2C"
                  width={100}
                  height={100}
                  className="max-w-xs md:hidden"
                  loading="lazy"
                  decoding="async"
                />
              </section>
              <div className="relative">
                {/* Both sizes below are the images' true intrinsic dimensions. They were 1200x700
                    and 600x400 — neither matched the files (1152x426 and 679x251), so the reserved
                    box had the wrong shape and the hero jumped when the image arrived. */}
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/vilvad2c_hero_Desktop_1.webp")}
                  alt="Vilva D2C dashboard on desktop"
                  width={1152}
                  height={426}
                  className="w-full max-w-6xl h-auto mx-auto"
                  priority
                  decoding="async"
                />

                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/vilvad2c_hero_mob_1.webp")}
                  alt="Vilva D2C dashboard on mobile"
                  width={679}
                  height={251}
                  className="w-full h-auto mx-auto block md:hidden"
                  priority
                  decoding="async"
                />
              </div>
            </m.div>
          </div>
        </div>
      </m.section>

      {/* Partners Section */}
      <TrustedPartners className="relative md:mt-[40px] mt-[32px]" />

      {/* Stats Section */}
      <m.section
        ref={statsRef}
        initial="hidden"
        animate={statsInView ? "visible" : "hidden"}
        variants={fadeIn}
        className="container"
      >
        {/* <div className="h-[0.8px] mb-[60px] opacity-[0.1] w-full bg-[#002E24]" /> */}
        <m.div
          variants={fadeIn}
          initial="hidden"
          animate={statsInView ? "visible" : "hidden"}
          transition={{ delay: 0.3 }}
          className="rounded-[16px] mb-[80px] md:mt-[40px] mt-[32px] lg:p-[24px]"
        >

          <section className="w-full">
            <div className="mx-auto max-w-7xl">
              <div
                className="rounded-[32px] bg-[linear-gradient(90deg,#F4FAFF_0%,#EEF6FF_50%,#F7FBFF_100%)] px-6 py-6 md:px-10 md:py-8">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 md:grid-cols-2 md:gap-0">
                  {stats.map((item, idx) => (
                    <div key={idx} className="relative pl-[16px]">
                      {/* Left vertical blue line */}
                      <span className="absolute left-0 top-1 h-[72px] w-[2px] bg-[#1667C3] md:h-[88px]" />

                      {/* Divider between items (only on md+) */}
                      {idx !== stats.length - 1 && (
                        <span className="absolute right-0 top-2 hidden h-[92px] w-[1px] bg-[#D7E7FF] md:block" />
                      )}

                      <p className="text-[#1667C3] text-[48px] leading-none font-[400] font-NeueMontreal">
                        <StatNumber
                          display={item.value}
                          exact={item.exact}
                          prefix={item.prefix}
                          target={item.target}
                          format={item.format}
                        />
                      </p>
                      <p className="mt-2 text-[14px] font-medium text-[#2D3748] md:text-[16px] text-[#000000]/80 font-Schibsted_Grotesk">
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>
                <UpdatedAt
                  iso={VILVAD2C_UPDATED_AT}
                  className="mt-5 text-center font-Schibsted_Grotesk text-[12px] font-normal text-[#000000]/50"
                />
              </div>
            </div>
          </section>
        </m.div>
      </m.section>

      {/* Real Stories From Real Businesses */}
      <section className="w-full bg-[#F7FCFF] py-[120px] px-4">
        <div className="max-w-7xl mx-auto">
          {/* Heading */}
          <div className="text-center max-w-7xl mx-auto mb-12">
            <h2 className="text-[36px] md:text-[48px] lg:text-[52px] font-bold text-[#0B1535] font-NeueMontreal">
              Real Stories From Real Businesses
            </h2>
            <p className="mt-[18px] text-[16px] md:text-[18px] text-[#5B6473] font-Schibsted_Grotesk">
              Hear how founders and teams use our platform to launch, sell, and scale.
            </p>
          </div>


          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px] items-stretch">
            {/* Video / Image */}
            <div className="relative md:rounded-[16px] rounded-[5px] overflow-hidden md:h-[569px] h-full">
              <div className="relative md:rounded-[16px] rounded-[5px] overflow-hidden md:h-[569px] h-full">
                {/* Responsive via CSS (not isMobile state): only the shown one is fetched, avoiding
                    a double-download + the hydration/CLS from swapping src & dimensions in JS. */}
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/real_story_img_Desktop_2.webp")}
                  alt="Real business success story with Vilva D2C"
                  width={800}
                  height={500}
                  className="hidden md:block md:w-[772px] md:h-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/real_story_img_mob_3.webp")}
                  alt="Real business success story with Vilva D2C"
                  width={400}
                  height={300}
                  className="block md:hidden w-auto object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>

            </div>

            {/* Stats + Quote */}
            <div className="flex flex-col justify-center">
              {/* Stats */}
              <div className="border-l-2 border-[#1667C3] pl-[24px] space-y-3">
                <div>
                  <p className="text-[48px] leading-none font-bold text-[#1667C3] font-NeueMontreal">
                    3.2X
                  </p>
                  <p className="text-[16px] text-[#000000] font-Schibsted_Grotesk font-[500]">
                    increase in monthly online revenue
                  </p>
                </div>

                <div>
                  <p className="text-[48px] leading-none font-bold text-[#1667C3] font-NeueMontreal">
                    28%
                  </p>
                  <p className="text-[16px] text-[#000000] font-Schibsted_Grotesk font-[500]">
                    checkout conversion improvement
                  </p>
                </div>

                <div>
                  <p className="text-[48px] leading-none font-bold text-[#1667C3] font-NeueMontreal">
                    42%
                  </p>
                  <p className="text-[16px] text-[#000000] font-Schibsted_Grotesk font-[500]">
                    recovery from abandoned carts
                  </p>
                </div>

                {/* Quote */}
                <p className="max-w-md pt-2 text-[#0B1535] text-[16px] md:text-[18px] font-[600] leading-relaxed font-Schibsted_Grotesk">
                  “We stopped worrying about the tech and started focusing on sales.
                  The checkout speed and automation alone recovered revenue we were
                  losing every day.”
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Dashboard Section */}
      <m.section
        ref={dashboardRef}
        initial="hidden"
        animate={dashboardInView ? "visible" : "hidden"}
        variants={fadeIn}
      >
        <div className="container mt-2 mx-auto row py-[40px]">
          <m.div
            variants={fadeIn}
            initial="hidden"
            animate={dashboardInView ? "visible" : "hidden"}
            transition={{ delay: 0.2 }}
            className="text-center pb-[20px] text-[#000] font-NeueMontreal text-[36px] md:text-[52px] font-black leading-normal"
          >
            Stop Building Stores. <span className="text-[#1667C3] font-NeueMontreal text-[36px] md:text-[52px] ">Start Making Sales.</span>
          </m.div>
          <m.div
            variants={fadeIn}
            initial="hidden"
            animate={dashboardInView ? "visible" : "hidden"}
            transition={{ delay: 0.3 }}
            className=""
          >
            <div className="">
              <main className="py-20">
                <section className="max-w-7xl mx-auto">
                  {/* Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-[24px]">
                    <FeatureCard
                      title="Easy Setup & Onboarding"
                      items={[
                        "Easy onboarding with zero friction",
                        "Ready to use defaults from day one",
                        "Guided setup for quick configuration",
                        "Build your store with our creative team",
                      ]}
                      cta="Get your store"
                      image={getImageUrl("/vilvabusiness/website/products/vilvad2c/build11_logo_1-desktop.webp")}
                      mobileImage={getImageUrl("/vilvabusiness/website/products/vilvad2c/build11_logo_2-mobile.webp")}
                    />

                    <FeatureCard
                      title="Payment & Global Selling"
                      items={[
                        "Multiple payment gateways supported",
                        "International selling without limitations",
                        "Multi currency pricing for global customers",
                        "Secure transactions with enterprise grade protection",
                      ]}
                      cta="Reach global customers without extra tools"
                      image={getImageUrl("/vilvabusiness/website/products/vilvad2c/Build2.webp")}
                      mobileImage={getImageUrl("/vilvabusiness/website/products/vilvad2c/build21_vilvad2c_1.webp")}
                    />

                    <FeatureCard
                      title="Performance & SEO"
                      items={[
                        "Next.js powered storefront for speed",
                        "SEO friendly pages with clean metadata",
                        "Optimized performance out of the box",
                        "Handles high traffic without slowdowns",
                      ]}
                      cta="Rank better · Load faster · Convert more"
                      image={getImageUrl("/vilvabusiness/website/products/vilvad2c/Build3.webp")}
                      mobileImage={getImageUrl("/vilvabusiness/website/products/vilvad2c/build3_vilvad2c_1.webp")}
                    />

                    <FeatureCard
                      title="Recovery & Automation"
                      items={[
                        "Recover abandoned carts automatically",
                        "Built in sales automation workflows",
                        "Smart follow ups that close sales",
                      ]}
                      cta="Recover lost revenue automatically"
                      image={getImageUrl("/vilvabusiness/website/products/vilvad2c/Build4.webp")}
                      mobileImage={getImageUrl("/vilvabusiness/website/products/vilvad2c/build4_vilvad2c_1.webp")}
                    />

                    <FeatureCard
                      title="Campaigns & Multistore"
                      items={[
                        "Launch and manage marketing campaigns centrally",
                        "Operate multiple stores without complexity",
                        "One dashboard for complete control",
                        "Assign roles and permissions securely",
                      ]}
                      cta="Start selling without technical setup"
                      image={getImageUrl("/vilvabusiness/website/products/vilvad2c/Build5.svg")}

                    />

                    <FeatureCard
                      title="Analytics & Reports"
                      items={[
                        "Track sales and revenue trends clearly",
                        "Understand product performance instantly",
                        "Monitor customer behavior and actions",
                        "Campaign effectiveness measured with clarity",
                      ]}
                      cta="Start selling without technical setup"
                      image={getImageUrl("/vilvabusiness/website/products/vilvad2c/Build6.webp")}

                    />
                  </div>
                </section>
              </main>
            </div>
          </m.div>
        </div>
      </m.section>

      {/* Store Designs */}
      <m.div
        ref={storeRef}
        initial="hidden"
        animate={storeInView ? "visible" : "hidden"}
        variants={fadeIn}
        className="md:py-[40px] py-[24px]"
      >
        <m.div
          variants={fadeIn}
          initial="hidden"
          animate={storeInView ? "visible" : "hidden"}
          transition={{ delay: 0.2 }}
          className="max-w-3xl lg:ml-[140px] md:ml-[80px] md:px-0 px-4"
        >
          <h3 className="font-black text-[28px] md:text-[44px] leading-[1.2] text-[#090D28]">
            <span className="text-[#1667C3] font-NeueMontreal">Launch Faster</span>{" "}
            <span className="font-NeueMontreal">
              with Ready-
              <br className="md:block hidden" />
              to-Convert Templates
            </span>
          </h3>

          <p className="mt-4 text-[#000000b3] font-Schibsted_Grotesk text-[14px] md:text-[18px] leading-[28px] max-w-7xl">
            Professionally designed templates built for performance,
            mobile responsiveness, and sales.
          </p>
        </m.div>

        <Swiper
          className="container pt-5 mx-auto mySwiper_store"
          modules={[Navigation, Scrollbar, Autoplay]}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          loop={true}
          scrollbar={{ draggable: true }}
          spaceBetween={0}
          slidesPerView={1}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
        >
          {mobileSlides.map(({ href, src, alt }, index) => (
            <SwiperSlide key={index} className="p-2 e_com_products">
              <m.a
                variants={fadeIn}
                initial="hidden"
                animate={storeInView ? "visible" : "hidden"}
                transition={{ delay: 0.3 + index * 0.1 }}
                href={href}
                target="_blank" rel="noopener noreferrer"
                className="group relative block overflow-hidden rounded-[8px]"
              >

                <img
                  className="img-fluid w-full h-[600px] object-contain object-top"
                  src={src}
                  alt={alt}
                  width={400}
                  height={300}
                />

                <div className="absolute inset-0 flex items-center justify-center bg-[#0B1535]/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <span className="flex items-center gap-2 rounded-[8px] bg-white px-6 py-3 font-Schibsted_Grotesk text-[15px] font-[600] text-[#0B1535]">
                    View Site
                    <FontAwesomeIcon icon={faArrowRight} className="text-[13px]" />
                  </span>
                </div>
              </m.a>
            </SwiperSlide>
          ))}
        </Swiper>
      </m.div>

      {/* Everything in One */}
      <m.section
        ref={everythingRef}
        initial="hidden"
        animate={everythingInView ? "visible" : "hidden"}
        variants={fadeIn}
      >
        <div className="bg-[#13233A] min-h-screen relative">
          <div className="max-w-7xl mx-auto text-center md:pt-[120px] pt-[60px] px-4">
            <h2 className="text-[#ffffff] text-[52px] font-NeueMontreal leading-[120%]">Everything in One <span className="text-[#529CF0] text-[52px] font-NeueMontreal leading-[120%]">Powerful Dashboard</span></h2>
            <p className="text-[#ffffff]/80 text-[18px] font-Schibsted_Grotesk leading-[120%]">Manage products, orders, payments, customers, and campaigns without switching tools or relying on developers.</p>
            <div className="mt-[50px]">
              {/* Responsive via CSS instead of isMobile state — see note on the real-story image. */}
              <Image
                src={getImageUrl("/vilvabusiness/website/products/vilvad2c/dashboard_vilvad2c_Desktop_1.svg")}
                alt="Vilva D2C all-in-one dashboard"
                width={720}
                height={760}
                className="lg:max-w-7xl max-w-5xl mx-auto z-50 w-full md:w-[658px] lg:w-[1149.55px] h-auto shadow-[-124px_-929px_250px_0_rgba(27,77,192,0),_-80px_-595px_240px_0_rgba(27,77,192,0.03),_-45px_-334px_202px_0_rgba(27,77,192,0.10),_-20px_-149px_150px_0_rgba(27,77,192,0.17),_-5px_-37px_82px_0_rgba(27,77,192,0.20)]"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </m.section >

      {/* Manage in-store sales */}
      <div >
        <div className="py-24 px-4">
          {/* Heading */}
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-[20px] md:text-[32px] text-[#1667C3] font-[600] font-NeueMontreal leading-[150%]">
              Manage in-store sales, online orders, and billing from a single platform.
            </p>
            <p className="mt-2 text-[16px] md:text-[32px] text-[#070D34] font-[600] font-Schibsted_Grotesk leading-[150%]">
              No separate POS tools. No manual reconciliation.
            </p>
          </div>

          {/* Cards */}
          <div className="mt-16 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Card 1 */}
            <div className="bg-[#3A6391] rounded-[24px] md:p-[40px] p-[24px] flex flex-col justify-between">
              <div>
                <p className="text-white text-[28px] md:text-[32px] font-NeueMontreal leading-tight">
                  Smart POS for physical stores
                </p>
                <p className="mt-4 text-white/95 text-[18px] md:text-[20px] font-Schibsted_Grotesk">
                  Turn your retail counter into a connected sales point that syncs
                  perfectly with your online store.
                </p>
              </div>

              <div className="mt-8">
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/Sales1.webp")}
                  alt="Smart POS system for physical stores, synced with your Vilva D2C online store"
                  width="1200"
                  height="675"
                  className="w-full h-auto rounded-[4px]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-[#D9EAFC] rounded-[24px] md:p-[40px] p-[24px] flex flex-col justify-between">
              <div>
                <p className="text-[#0B1535] text-[28px] md:text-[32px] font-NeueMontreal leading-tight">
                  Billing App for Stores
                </p>
                <p className="mt-4 text-[#000000]/80 text-[18px] md:text-[20px] font-Schibsted_Grotesk">
                  Capture invoices and bills using your mobile app and automatically
                  update products, inventory, and expenses.
                </p>
              </div>

              <div className="mt-8">
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvad2c/Sales2.webp")}
                  alt="Billing app that captures invoices and auto-updates inventory and expenses"
                  width="1200"
                  height="675"
                  className="w-full h-auto rounded-[4px]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

          </div>
        </div>
      </div >



      {/* Pricing Section */}
      < m.section
        ref={pricingRef}
        initial="hidden"
        animate={pricingInView ? "visible" : "hidden"}
        variants={fadeIn}
      >
        <EcomPricingPlan />
      </m.section >

      {/* Why Teams Love Working With Us */}
      <div className="py-[60px] md:py-[100px] px-4 md:px-0 md:container">
        <p className="text-[#090D28] text-center text-[32px] md:text-[48px] leading-[120%] font-NeueMontreal">Why teams love{" "}
          <span className="text-[32px] md:text-[48px]  leading-[120%] font-NeueMontreal bg-gradient-to-r from-[#1586FF] to-[#003FDF] bg-clip-text text-transparent"
          >Working With Us</span> </p>
        <p className="text-[#000]/70 text-center text-[16px] md:text-[18px] font-[400] leading-[150%] font-Schibsted_Grotesk py-4">When our customers win, we win. Here’s how they’ve used our tools to succeed.</p>

        <section className="flex items-center justify-center gap-3">
          <Image
            src="/images/assets/Google-review.svg"
            width={220}
            height={220}
            alt="Google review tag"
            quality={75}
            className=""
            style={{ width: 'auto', height: 'auto' }}
          />
          <Image
            src="/images/assets/Just-dail.svg"
            width={220}
            height={220}
            alt="Justdial review tag"
            quality={75}
            className=""
            style={{ width: 'auto', height: 'auto' }}
          />
        </section>

        {/* <section className="md:container px-[16px] pt-[20px] md:pt-[80px]">
          <Testimonials page="vilvad2c" />
        </section> */}
          <section className="md:container px-[16px] pt-[20px] md:pt-[80px] flex justify-center items-center">
                  <CircularTestimonialView page="vilvad2c" />
                </section>
      </div>

      {/* Slightly more aggressive Less */}
      <m.section
        ref={freedomRef}
        initial="hidden"
        animate={freedomInView ? "visible" : "hidden"}
        variants={fadeIn}
        className="w-full px-4 md:px-8 py-10"
      >
        <div className="max-w-7xl mx-auto bg-[#CEE2F9] rounded-[20px] p-6 md:p-12">
          <div className="flex flex-col md:flex-row items-center gap-[17px]">

            {/* LEFT CONTENT */}
            <m.div
              variants={fadeIn}
              initial="hidden"
              animate={freedomInView ? "visible" : "hidden"}
              transition={{ delay: 0.2 }}
              className="w-full md:w-1/2"
            >
              <p className="text-[#0B1535] font-NeueMontreal text-[28px] md:text-[48px] font-normal leading-[120%] mb-4 max-w-[800px]">
                Slightly more aggressive Less tools. More revenue.

              </p>

              <p className="text-[#3B3F4A] font-Schibsted_Grotesk text-[16px] md:text-[20px] leading-normal mb-8 max-w-xl">
                Build, launch, and grow your online store without complexity.
              </p>

              <div className="md:flex flex flex-row  gap-3">
                <m.a
                  variants={fadeIn}
                  transition={{ delay: 0.3 }}
                  href="https://admin.vilvad2c.com/register"
                  target="_blank" rel="noopener noreferrer"
                  className="h-[52px] px-8 flex items-center justify-center rounded-[8px] bg-[#195AFF] text-white font-Schibsted_Grotesk text-[18px] text-nowrap"
                >
                  Get Started
                </m.a>

                <m.a
                  variants={fadeIn}
                  transition={{ delay: 0.35 }}
                  href="https://admin.vilvad2c.com/register"
                  target="_blank" rel="noopener noreferrer"
                  className="h-[52px] px-8 flex items-center justify-center rounded-[8px] border-2 border-[#6F9CFF] bg-[#EFF4FF] text-[#195AFF] font-Schibsted_Grotesk text-[18px] text-nowrap"
                  aria-label="Book Demo — opens Vilva D2C sign-up in a new tab"
                >
                  Book Demo
                </m.a>
              </div>
            </m.div>

            {/* RIGHT IMAGE */}
            <m.div
              variants={fadeIn}
              initial="hidden"
              animate={freedomInView ? "visible" : "hidden"}
              transition={{ delay: 0.4 }}
              className="w-full md:w-1/2 flex justify-center"
            >
              <Image
                src={getImageUrl("/vilvabusiness/website/products/vilvad2c/Revenue.webp")}
                alt="Vilva D2C dashboard highlighting store revenue growth"
                width="720"
                height="760"
                className="hidden md:block md:w-1/2 h-auto"
                loading="lazy"
                decoding="async"
              />
            </m.div>
          </div>
        </div>
      </m.section>

      {/* FAQ Section */}
      <section className="pb-[60px] md:pb-[120px] px-4 md:px-0 md:container">
        {/* <h2 className="text-[#090D28] text-center md:text-[] text-[32px] w-full mx-auto whitespace-normal md:whitespace-nowrap md:text-[40px] font-extrabold leading-[120%] font-LatinkaBold">
          Your questions Our Answers
        </h2> */}
        <FaqSection faqs={faqs} />
      </section>
    </div >
  );
};

export default VilvaD2C;